import * as productApi from '@apis/app-products';
import { ReactComponent as StarOn } from '@assets/images/app/star-pink.svg';
import { Carousel } from '@components/common/Carousel';
import { ProductModel } from '@models/product';
import { ProductReview, REVIEW_FACE_VALUES } from '@models/user-contents';
import { useQuery } from '@tanstack/react-query';
import { cdnImageUrl } from '@utils/image';
import React from 'react';
import ClipLoader from 'react-spinners/ClipLoader';

export const ProductReviewSummary: React.FC<{ data: ProductModel }> = (props) => {
  const { data } = props;
  const productId = data._id;
  const productReviewsQuery = useQuery({
    queryKey: [`product-reviews-${productId}`],
    queryFn: () => productApi.getProductReviews(productId),
    enabled: !!productId,
  });

  const reviews = productReviewsQuery.data?.data?.contents || [];
  const totalCount = productReviewsQuery.data?.data?.total || 0;

  if (productReviewsQuery.isLoading) {
    return <ClipLoader color="#FF3D8F" />;
  }

  if (totalCount === 0) {
    return <></>;
  }

  return (
    <div
      style={{
        padding: '20px',
        paddingBottom: 0,
        marginBottom: 0,
        backgroundColor: '#ffffff',
      }}>
      {reviews.length > 0 && (
        <div>
          <div
            style={{
              color: 'var(--Neutral-100, #242424)',
              fontFamily: 'Pretendard',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 700,
              marginBottom: '16px',
            }}>
            <span>생생후기</span>
            {totalCount > 0 && <span style={{ color: '#FF3D8F', marginLeft: '4px' }}>{totalCount}</span>}
          </div>
          <div>
            <Carousel
              options={{
                dragFree: true,
                align: 'start',
              }}>
              {reviews.map((item: ProductReview, index: number) => {
                const reviewRate = Number(
                  (
                    ((item.rates?.rate1 || 0) +
                      (item.rates?.rate2 || 0) +
                      (item.rates?.rate3 || 0) +
                      (item.rates?.rate4 || 0)) /
                    4
                  ).toFixed(1),
                );
                const childSatisfaction = item.rates?.rate5;
                const ChildSatisfactionIcon = childSatisfaction
                  ? REVIEW_FACE_VALUES[`${Math.round(childSatisfaction)}`]?.Icon
                  : undefined;
                const childSatisfactionText = childSatisfaction
                  ? REVIEW_FACE_VALUES[`${Math.round(childSatisfaction)}`]?.label
                  : undefined;

                return (
                  <div
                    key={`review-carousel-item-${index}`}
                    className={'embla__slide'}
                    style={{
                      flex: `0 0 300px`,
                      background: '#F5F7FB',
                      border: '1px solid #E8EAED',
                      borderRadius: '12px',
                      marginRight: '8px',
                      cursor: 'pointer',
                    }}>
                    <div
                      style={{
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        height: '100%',
                      }}>
                      {/* Caption 섹션 */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}>
                        {/* Left - Star & Rating */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}>
                          <StarOn style={{ width: '16px', height: '16px' }} />
                          <div
                            style={{
                              color: '#424242',
                              fontFamily: 'Pretendard',
                              fontSize: '14px',
                              fontWeight: 700,
                              lineHeight: '1.43',
                            }}>
                            {reviewRate}
                          </div>
                        </div>

                        {/* Info - Location & Age */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                          }}>
                          <div
                            style={{
                              color: '#808387',
                              fontFamily: 'Pretendard',
                              fontSize: '13px',
                              fontWeight: 400,
                              lineHeight: '1.38',
                            }}>
                            {item.publisherInfo?.addressText}
                          </div>
                          <div
                            style={{
                              color: '#9B9DA0',
                              fontFamily: 'Pretendard',
                              fontSize: '13px',
                              fontWeight: 400,
                              lineHeight: '1.38',
                              width: '4px',
                            }}>
                            ·
                          </div>
                          <div
                            style={{
                              color: '#808387',
                              fontFamily: 'Pretendard',
                              fontSize: '13px',
                              fontWeight: 400,
                              lineHeight: '1.38',
                            }}>
                            {item.publisherInfo?.childrenAgeText}
                          </div>
                          {/* BEST Label */}
                          {item.bestDate && (
                            <div
                              style={{
                                border: '1px solid #FF3D8F',
                                borderRadius: '4px',
                                padding: '2px 4px',
                                marginLeft: '4px',
                              }}>
                              <div
                                style={{
                                  color: '#FF3D8F',
                                  fontFamily: 'Pretendard',
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  lineHeight: '1.27',
                                }}>
                                BEST
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Content 섹션 */}
                      <div
                        style={{
                          display: 'flex',
                          gap: '12px',
                        }}>
                        {/* Text */}
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            flex: 1,
                          }}>
                          {item.title && (
                            <div
                              style={{
                                color: '#242424',
                                fontFamily: 'Pretendard',
                                fontSize: '14px',
                                fontWeight: 700,
                                lineHeight: '1.43',
                              }}>
                              {item.title}
                            </div>
                          )}
                          <div
                            style={{
                              color: '#424242',
                              fontFamily: 'Pretendard',
                              fontSize: '14px',
                              fontWeight: 400,
                              lineHeight: '1.43',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}>
                            {item.description || item.text}
                          </div>
                        </div>

                        {/* Photo */}
                        {item.images && item.images.length > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                            }}>
                            <div
                              style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '8px',
                                background: '#F8F9FC',
                                border: '0.4px solid rgba(0, 0, 0, 0.1)',
                                position: 'relative',
                                overflow: 'hidden',
                              }}>
                              <img
                                src={cdnImageUrl(item.images[0])}
                                alt="review"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                              {/* Badge */}
                              {item.images && item.images.length > 1 && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    background: 'rgba(0, 0, 0, 0.4)',
                                    borderRadius: '8px 0px 8px 0px',
                                    padding: '2px 7px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}>
                                  <div
                                    style={{
                                      color: '#FFFFFF',
                                      fontFamily: 'Pretendard',
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      lineHeight: '1.43',
                                    }}>
                                    {item.images.length}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Bottom - Child Satisfaction */}
                      {childSatisfaction !== undefined && childSatisfaction > 0 && ChildSatisfactionIcon && (
                        <>
                          {/* Divider */}
                          <div
                            style={{
                              height: '1px',
                              background: '#E8EAED',
                              width: '100%',
                            }}
                          />
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}>
                            {ChildSatisfactionIcon && (
                              <ChildSatisfactionIcon style={{ width: '20px', height: '20px' }} />
                            )}
                            <div
                              style={{
                                color: '#FF3D8F',
                                fontFamily: 'Pretendard',
                                fontSize: '14px',
                                fontWeight: 700,
                                lineHeight: '1.43',
                              }}>
                              {`"${childSatisfactionText}"`}
                            </div>
                          </div>
                        </>
                      )}

                      {/* Product Info */}
                      <div
                        style={{
                          marginTop: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                        }}>
                        {/* Divider */}
                        <div
                          style={{
                            height: '1px',
                            background: '#E8EAED',
                            width: '100%',
                            marginBottom: '12px',
                          }}
                        />
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            cursor: 'pointer',
                          }}>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px',
                              flex: 1,
                            }}>
                            <div
                              style={{
                                color: '#424242',
                                fontFamily: 'Pretendard',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: '1.43',
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>
                              {item.product?.title}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Carousel>
          </div>
        </div>
      )}
      <div style={{ height: '1px', background: '#E8EAED', width: '100%', marginTop: '20px' }} />
    </div>
  );
};
